import type { FeatureName } from '@hooks/useFeature'

/**
 * Dashboard gating by intelligence module, keyed by the report `value` from
 * getTableList. A portfolio turns these on and off in Account Info -> Analytics
 * (Activate-internal); a module that is off drops its reports out of
 * getBuildingReports, which is what every picker, the category tabs and the
 * sidebar all derive from — so the nav entry disappears with the dashboard.
 *
 * Reports absent from this map are never feature-gated. Both scopes of a
 * dashboard (building and its Global/portfolio twin) map to the same module:
 * a module the customer does not have should not survive at portfolio level.
 *
 * Keep in sync with `backend/framework/Services.js` Feature tags — the backend
 * refuses the same calls with auth.feature.disabled.group.
 */
export const DASHBOARD_FEATURE: Record<string, FeatureName> = {
  // Equipment Intelligence — the dashboard family, not the operational
  // equipment records (AED/fire extinguisher rounds keep the Equipment flag).
  EquipmentDashboard: 'EquipmentIntelligence',
  GlobalEquipmentDashboard: 'EquipmentIntelligence',
  EquipmentLookup: 'EquipmentIntelligence',
  EquipmentTrend: 'EquipmentIntelligence',
  EquipmentDataIntegrity: 'EquipmentIntelligence',
  GlobalEquipmentDataIntegrity: 'EquipmentIntelligence',
  EquipmentDetailCollection: 'EquipmentIntelligence',

  // Lease Intelligence
  LeaseAbstraction: 'LeaseIntelligence',
  GlobalLeaseAbstraction: 'LeaseIntelligence',

  // Capital Planning
  CapitalPlanning: 'CapitalPlanning',
  GlobalCapitalPlanning: 'CapitalPlanning',

  // Work Order Intelligence. The Work Order Dashboard itself is deliberately
  // absent: it is the operational dashboard and follows the WorkOrders flag.
  WorkOrderIntelligence: 'WorkOrderIntelligence',

  // Roof Intelligence
  RoofAbstraction: 'RoofIntelligence',
  GlobalRoofAbstraction: 'RoofIntelligence',

  // Cleaning Intelligence (Activate Performance IQ). The plain 'Cleaning' round
  // report is an inspection report, not the IQ module, so it stays ungated.
  CleaningIntelligence: 'CleaningIntelligence',
  GlobalCleaningIQ: 'CleaningIntelligence',

  // Engineering Intelligence (specs#3482). The plain 'Engineering' round report stays
  // ungated for the same reason.
  EngineeringIntelligence: 'EngineeringIntelligence',
  GlobalEngineeringIQ: 'EngineeringIntelligence',

  // Security Intelligence (specs#3482). The plain 'SecurityPatrol' round report stays ungated.
  SecurityIntelligence: 'SecurityIntelligence',
  GlobalSecurityIQ: 'SecurityIntelligence',

  // PM Intelligence
  PMCompliance: 'PMIntelligence',
  GlobalPMCompliance: 'PMIntelligence',
  PreventativeMaintenance: 'PMIntelligence',
  PMLibraryDashboard: 'PMIntelligence',

  // Compliance Intelligence
  ComplianceRecords: 'ComplianceIntelligence',
  GlobalComplianceRecords: 'ComplianceIntelligence',
}
